// Analytics Service - Handles Google Analytics and Facebook Pixel integration

interface AnalyticsConfig {
  googleAnalyticsId?: string;
  facebookPixelId?: string;
}

let initialized = false;
let config: AnalyticsConfig = {};

// Initialize Google Analytics
const initGoogleAnalytics = (trackingId: string) => {
  if (!trackingId || typeof window === 'undefined') return;

  // Check if already loaded
  if (document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) return;

  // Load gtag.js
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingId}`;
  document.head.appendChild(script);

  // Initialize gtag
  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(...args: any[]) {
    (window as any).dataLayer.push(args);
  }
  (window as any).gtag = gtag;
  gtag('js', new Date());
  gtag('config', trackingId, {
    send_page_view: true
  });

  console.log('Google Analytics initialized:', trackingId);
};

// Initialize Facebook Pixel
const initFacebookPixel = (pixelId: string) => {
  if (!pixelId || typeof window === 'undefined') return;

  // Check if already loaded
  if ((window as any).fbq) return;

  // Facebook Pixel code
  (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  (window as any).fbq('init', pixelId);
  (window as any).fbq('track', 'PageView');

  console.log('Facebook Pixel initialized:', pixelId);
};

// Initialize all analytics
export const initAnalytics = async (userId?: string) => {
  if (initialized || !userId) return;

  try {
    const token = localStorage.getItem('token');
    if (!token) return;

    const API_URL = process.env.REACT_APP_API_URL || 'https://templimizeai-coursecreatorvault.onrender.com/api';

    const response = await fetch(`${API_URL}/integrations`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) return;

    const integrations = await response.json();

    // Find active analytics integrations
    const gaIntegration = integrations.find(
      (i: any) => i.integration_type === 'google_analytics' && i.is_active
    );
    const fbIntegration = integrations.find(
      (i: any) => i.integration_type === 'facebook_pixel' && i.is_active
    );

    if (gaIntegration?.config?.trackingId) {
      config.googleAnalyticsId = gaIntegration.config.trackingId;
      initGoogleAnalytics(gaIntegration.config.trackingId);
    }

    if (fbIntegration?.config?.pixelId) {
      config.facebookPixelId = fbIntegration.config.pixelId;
      initFacebookPixel(fbIntegration.config.pixelId);
    }

    initialized = true;
  } catch (error) {
    console.error('Error initializing analytics:', error);
  }
};

// Track page view
export const trackPageView = (pagePath?: string) => {
  if (typeof window === 'undefined') return;

  const path = pagePath || window.location.pathname;

  // Google Analytics
  if (config.googleAnalyticsId && (window as any).gtag) {
    (window as any).gtag('config', config.googleAnalyticsId, {
      page_path: path
    });
  }

  // Facebook Pixel
  if (config.facebookPixelId && (window as any).fbq) {
    (window as any).fbq('track', 'PageView');
  }
};

// Track custom event
export const trackEvent = (eventName: string, params?: Record<string, any>) => {
  if (typeof window === 'undefined') return;

  // Google Analytics
  if (config.googleAnalyticsId && (window as any).gtag) {
    (window as any).gtag('event', eventName, params);
  }

  // Facebook Pixel
  if (config.facebookPixelId && (window as any).fbq) {
    (window as any).fbq('trackCustom', eventName, params);
  }
};

// Track course creation
export const trackCourseCreated = (courseTitle: string, courseId: string) => {
  trackEvent('course_created', {
    course_title: courseTitle,
    course_id: courseId
  });

  // Facebook specific
  if ((window as any).fbq) {
    (window as any).fbq('track', 'Lead', {
      content_name: courseTitle,
      content_ids: [courseId],
      content_type: 'course'
    });
  }
};

// Track course published
export const trackCoursePublished = (courseTitle: string, courseId: string) => {
  trackEvent('course_published', {
    course_title: courseTitle,
    course_id: courseId
  });
};

// Track export
export const trackExport = (format: string, courseTitle: string) => {
  trackEvent('course_exported', {
    export_format: format,
    course_title: courseTitle
  });
};

// Track subscription/purchase
export const trackPurchase = (tier: string, amount: number) => {
  trackEvent('purchase', {
    tier: tier,
    value: amount,
    currency: 'USD'
  });

  // Facebook specific
  if ((window as any).fbq) {
    (window as any).fbq('track', 'Purchase', {
      value: amount,
      currency: 'USD',
      content_name: tier
    });
  }
};
